import React, { useCallback, useRef, useState } from 'react';
import { ProcedureStep } from '../../../../../types/ProcedureStep';
import styled from 'styled-components';
import { format } from 'date-fns';
import { useMutation } from '@apollo/client';
import { orTabletProcedureSteps, setProcedureStepTimestamp } from '../../../../../graph/procedureSteps';
import { Patient } from '../../../../../types/Patient';
import { setTimeToDate } from '../util/procedureSteps';

const Timestamp = styled.div<{ first: boolean }>`
  > * {
    margin: 0;
  }

  padding-top: 3vh;
  padding-bottom: 3vh;

  @media (orientation: landscape) {
    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
    }
  }

  border-bottom: 1px solid rgba(255, 255, 255, 0.2);

  &:last-child {
    border-bottom: none;
  }

  @media (orientation: portrait) {
    padding-top: 1.6rem;
    padding-bottom: 1.6rem;
  }
`;

const TimestampName = styled.div<{ highlight?: boolean }>`
  font-style: normal;
  font-size: calc(1.2rem + 0.390625vw);
  color: ${props => (props.highlight ? '#00A7F7' : 'rgb(222, 236, 243, 0.5)')};
`;

const TimestampValue = styled.div<{ highlight?: boolean }>`
  font-style: normal;
  font-weight: 700;
  font-size: calc(1.8rem + 0.390625vw);

  color: ${props => (props.highlight ? '#00A7F7' : '#ffffff')};
`;

const TimestampValueInput = styled.input<{ highlight?: boolean }>`
  font-style: normal;
  font-weight: 700;
  font-size: calc(1.8rem + 0.390625vw);
  color: ${props => (props.highlight ? '#00A7F7' : '#ffffff')};
  z-index: 3;
  position: relative;
  outline: none;
  border: 2px solid #00a7f7;
  border-radius: 0.6rem;
  background-color: #1f2c61;
  padding: 0 1rem;

  &::-webkit-calendar-picker-indicator {
    display: none;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 2;
`;

const ProcedureStepTime = ({
  index,
  procedureStep,
  patient,
}: {
  index: number;
  procedureStep: ProcedureStep & { highlight: boolean; show: boolean };
  patient?: Patient & { duration?: string | undefined };
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [save] = useMutation(setProcedureStepTimestamp);

  const initialTime =
    procedureStep?.show && procedureStep?.timestamp ? format(procedureStep?.timestamp, 'HH:mm') : undefined;
  const [edit, setEdit] = useState(false);
  const [time, setTime] = useState(initialTime);

  const canEdit = !!initialTime;
  const trySetEdit = useCallback(() => {
    canEdit && !edit && setEdit(value => !value);
    inputRef?.current?.focus();
    inputRef?.current?.showPicker();
  }, [canEdit, edit]);

  const saveAndClose = useCallback(async () => {
    patient?.id &&
      (await save({
        variables: {
          patientId: patient?.id,
          procedureStepId: procedureStep?.id,
          procedureStepType: procedureStep?.type,
          timestamp: setTimeToDate(new Date(), time),
        },
        refetchQueries: [{ query: orTabletProcedureSteps, variables: { patientId: patient?.id } }],
      }));
    setEdit(false);
  }, [patient, procedureStep, time, save]);

  return (
    <>
      {edit && <Overlay onClick={saveAndClose} />}
      <Timestamp first={index === 0} onClick={trySetEdit}>
        <TimestampName highlight={procedureStep?.highlight}>{procedureStep?.name}</TimestampName>
        {!edit && <TimestampValue highlight={procedureStep?.highlight}>{initialTime || '-'}</TimestampValue>}
        {edit && (
          <TimestampValueInput
            ref={inputRef}
            type="time"
            value={time}
            onChange={e => {
              setTime(e?.target?.value);
            }}
          />
        )}
      </Timestamp>
    </>
  );
};

export default ProcedureStepTime;
